import { useMemo, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '@/context/AppContext';
import type { Goal, GoalStatus, TimeHorizon, GoalCategory, GoalProgressResult, KeyResult, GoalCheckIn } from '@trackmind/core';
import { computeAllGoalProgress, applyGoalUpdate, goalDeletionEffect, createGoal as createCoreGoal } from '@trackmind/core';
import { useToday } from '@/hooks/useToday';

export interface CreateGoalInput {
  title: string;
  description?: string;
  time_horizon?: TimeHorizon;
  category?: GoalCategory;
  target_date?: string;
  linked_project_ids?: string[];
  linked_habit_ids?: string[];
  linked_task_ids?: string[];
  parent_goal_id?: string;
}

export interface UpdateGoalInput {
  title?: string;
  description?: string;
  time_horizon?: TimeHorizon;
  category?: GoalCategory;
  target_date?: string;
  linked_project_ids?: string[];
  linked_habit_ids?: string[];
  linked_task_ids?: string[];
  parent_goal_id?: string;
  status?: GoalStatus;
  /** Manual override, 0-100; null clears it and returns to derived roll-up */
  progress_override?: number | null;
  key_results?: KeyResult[];
  check_ins?: GoalCheckIn[];
  achieved_at?: string | null;
}

/**
 * Progress shape. Now an alias for the core result, which is a superset of the
 * old locally-defined interface — the roll-up lives in
 * @trackmind/core/services/goal-progress so it can be pure and recursive.
 */
export type GoalProgress = GoalProgressResult;

export function useGoals(filters?: { status?: GoalStatus; timeHorizon?: TimeHorizon; category?: GoalCategory }) {
  const { goals, saveGoal, deleteGoal: removeGoal, tasks, projects, habitInstances } = useApp();
  const todayKey = useToday();

  // Apply filters
  const filteredGoals = useMemo(() => {
    let result = goals;
    if (filters?.status) {
      result = result.filter(g => g.status === filters.status);
    }
    if (filters?.timeHorizon) {
      result = result.filter(g => g.time_horizon === filters.timeHorizon);
    }
    if (filters?.category) {
      result = result.filter(g => g.category === filters.category);
    }
    return result;
  }, [goals, filters]);

  // Latest derived progress, readable from callbacks without making them depend
  // on it (progressById is computed further down, after the mutators).
  const progressByIdRef = useRef<Record<string, GoalProgressResult>>({});

  const createGoal = useCallback(async (data: CreateGoalInput): Promise<Goal> => {
    // Core's factory, shared with the assistant (see createGoal in core).
    const newGoal = createCoreGoal(data);
    await saveGoal(newGoal);
    return newGoal;
  }, [saveGoal]);

  const updateGoal = useCallback(async (id: string, data: UpdateGoalInput): Promise<Goal> => {
    const existing = goals.find(g => g.id === id);
    if (!existing) {
      throw new Error(`Goal ${id} not found`);
    }
    // applyGoalUpdate in core, so the app and the assistant merge a goal
    // change the same way — including the achieved_at rule, which is the
    // part a second implementation would get wrong.
    const updated = applyGoalUpdate(existing, data, new Date().toISOString());

    await saveGoal(updated);
    return updated;
  }, [goals, saveGoal]);

  // ── Key results ───────────────────────────────────────────────────────────

  const addKeyResult = useCallback(async (
    goalId: string,
    label: string,
    target: number,
    unit?: string | null,
  ): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) throw new Error(`Goal ${goalId} not found`);
    const now = new Date().toISOString();
    const kr: KeyResult = {
      id: uuidv4(),
      label,
      target: Math.max(1, target),
      current: 0,
      unit: unit ?? null,
      created_at: now,
      updated_at: now,
    };
    return updateGoal(goalId, { key_results: [...(existing.key_results ?? []), kr] });
  }, [goals, updateGoal]);

  const updateKeyResult = useCallback(async (
    goalId: string,
    krId: string,
    patch: Partial<Pick<KeyResult, 'label' | 'target' | 'current' | 'unit'>>,
  ): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) throw new Error(`Goal ${goalId} not found`);
    const next = (existing.key_results ?? []).map(kr =>
      kr.id === krId
        ? {
            ...kr,
            ...patch,
            // Never let the count go negative or the target below 1
            current: patch.current !== undefined ? Math.max(0, patch.current) : kr.current,
            target: patch.target !== undefined ? Math.max(1, patch.target) : kr.target,
            updated_at: new Date().toISOString(),
          }
        : kr
    );
    return updateGoal(goalId, { key_results: next });
  }, [goals, updateGoal]);

  /** One-tap increment/decrement — the primary way to move a KR on a phone. */
  const stepKeyResult = useCallback(async (
    goalId: string,
    krId: string,
    delta: number,
  ): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) throw new Error(`Goal ${goalId} not found`);
    const kr = (existing.key_results ?? []).find(k => k.id === krId);
    if (!kr) throw new Error(`Key result ${krId} not found`);
    return updateKeyResult(goalId, krId, { current: kr.current + delta });
  }, [goals, updateKeyResult]);

  const deleteKeyResult = useCallback(async (goalId: string, krId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) throw new Error(`Goal ${goalId} not found`);
    return updateGoal(goalId, {
      key_results: (existing.key_results ?? []).filter(kr => kr.id !== krId),
    });
  }, [goals, updateGoal]);

  // ── Check-ins ─────────────────────────────────────────────────────────────

  /**
   * Record where the goal stands today, plus an optional note. The percentage
   * is captured from the derived roll-up rather than typed, so the history is
   * a truthful trend and the note carries the context.
   */
  const addCheckIn = useCallback(async (goalId: string, note?: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) throw new Error(`Goal ${goalId} not found`);
    // The user's today. The UTC date filed an evening check-in in the
    // Americas under tomorrow — and "one per day" then kept the wrong one.
    const today = todayKey();
    const snapshot = progressByIdRef.current[goalId]?.progress ?? 0;
    const entry: GoalCheckIn = {
      id: uuidv4(),
      date: today,
      progress: snapshot,
      note: note?.trim() || null,
      created_at: new Date().toISOString(),
    };
    // One check-in per day: a second one replaces the first rather than
    // cluttering the trend with same-day duplicates.
    const rest = (existing.check_ins ?? []).filter(c => c.date !== today);
    const next = [...rest, entry].sort((a, b) => a.date.localeCompare(b.date));
    return updateGoal(goalId, { check_ins: next });
  }, [goals, updateGoal, todayKey]);

  const deleteCheckIn = useCallback(async (goalId: string, checkInId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) throw new Error(`Goal ${goalId} not found`);
    return updateGoal(goalId, {
      check_ins: (existing.check_ins ?? []).filter(c => c.id !== checkInId),
    });
  }, [goals, updateGoal]);

  /**
   * Delete a goal and re-home its children rather than orphaning them.
   * Previously the goal row was removed on its own, leaving sub-goals with a
   * dangling parent_goal_id — they vanished from the tree (which only renders
   * top-level goals and their descendants) without being deleted.
   */
  const deleteGoal = useCallback(async (id: string): Promise<void> => {
    // `goalDeletionEffect` in core decides; this saves what it decided. The
    // assistant's delete calls the same function, so a sub-goal is re-homed
    // the same way whichever door the delete came through.
    const { reparented } = goalDeletionEffect(goals, id, new Date().toISOString());
    for (const child of reparented) await saveGoal(child);
    await removeGoal(id);
  }, [goals, saveGoal, removeGoal]);

  const linkProject = useCallback(async (goalId: string, projectId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) {
      throw new Error(`Goal ${goalId} not found`);
    }
    if (existing.linked_project_ids.includes(projectId)) {
      return existing;
    }
    return updateGoal(goalId, {
      linked_project_ids: [...existing.linked_project_ids, projectId],
    });
  }, [goals, updateGoal]);

  const unlinkProject = useCallback(async (goalId: string, projectId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) {
      throw new Error(`Goal ${goalId} not found`);
    }
    return updateGoal(goalId, {
      linked_project_ids: existing.linked_project_ids.filter(id => id !== projectId),
    });
  }, [goals, updateGoal]);

  const linkHabit = useCallback(async (goalId: string, habitId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) {
      throw new Error(`Goal ${goalId} not found`);
    }
    const linkedHabitIds = existing.linked_habit_ids ?? [];
    if (linkedHabitIds.includes(habitId)) {
      return existing;
    }
    return updateGoal(goalId, {
      linked_habit_ids: [...linkedHabitIds, habitId],
    });
  }, [goals, updateGoal]);

  const unlinkHabit = useCallback(async (goalId: string, habitId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) {
      throw new Error(`Goal ${goalId} not found`);
    }
    const linkedHabitIds = existing.linked_habit_ids ?? [];
    return updateGoal(goalId, {
      linked_habit_ids: linkedHabitIds.filter(id => id !== habitId),
    });
  }, [goals, updateGoal]);

  const linkTask = useCallback(async (goalId: string, taskId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) {
      throw new Error(`Goal ${goalId} not found`);
    }
    const linkedTaskIds = existing.linked_task_ids ?? [];
    if (linkedTaskIds.includes(taskId)) {
      return existing;
    }
    return updateGoal(goalId, {
      linked_task_ids: [...linkedTaskIds, taskId],
    });
  }, [goals, updateGoal]);

  const unlinkTask = useCallback(async (goalId: string, taskId: string): Promise<Goal> => {
    const existing = goals.find(g => g.id === goalId);
    if (!existing) {
      throw new Error(`Goal ${goalId} not found`);
    }
    const linkedTaskIds = existing.linked_task_ids ?? [];
    return updateGoal(goalId, {
      linked_task_ids: linkedTaskIds.filter(id => id !== taskId),
    });
  }, [goals, updateGoal]);

  /**
   * Derived progress for every goal, keyed by id. Synchronous — components
   * read straight out of this during render.
   */
  // The habit completion records themselves: core counts them inside EACH
  // goal's window. This hook used to fetch counts per habit from the earliest
  // window of any goal linking it — so a later goal sharing the habit counted
  // ticks from before it existed — and the assistant counted all-time, and a
  // review its own period: one goal, three percentages.
  const progressById = useMemo(
    () => computeAllGoalProgress(goals, tasks, projects, habitInstances ?? []),
    [goals, tasks, projects, habitInstances],
  );

  useEffect(() => { progressByIdRef.current = progressById; }, [progressById]);

  /** Derived progress for one goal (undefined only for an unknown id). */
  const getProgress = useCallback(
    (goalId: string): GoalProgressResult | undefined => progressById[goalId],
    [progressById],
  );

  // Organize goals by time horizon
  const yearlyGoals = useMemo(() => filteredGoals.filter(g => g.time_horizon === 'yearly'), [filteredGoals]);
  const quarterlyGoals = useMemo(() => filteredGoals.filter(g => g.time_horizon === 'quarterly'), [filteredGoals]);
  const monthlyGoals = useMemo(() => filteredGoals.filter(g => g.time_horizon === 'monthly'), [filteredGoals]);

  // Active goals
  const activeGoals = useMemo(() => filteredGoals.filter(g => g.status === 'active'), [filteredGoals]);
  const achievedGoals = useMemo(() => filteredGoals.filter(g => g.status === 'achieved'), [filteredGoals]);
  const abandonedGoals = useMemo(() => filteredGoals.filter(g => g.status === 'abandoned'), [filteredGoals]);

  // Build hierarchy tree
  const getChildGoals = useCallback((parentId: string) =>
    filteredGoals.filter(g => g.parent_goal_id === parentId), [filteredGoals]);
  const topLevelGoals = useMemo(() => filteredGoals.filter(g => !g.parent_goal_id), [filteredGoals]);

  return {
    goals: filteredGoals,
    yearlyGoals,
    quarterlyGoals,
    monthlyGoals,
    activeGoals,
    achievedGoals,
    abandonedGoals,
    topLevelGoals,
    getChildGoals,
    createGoal,
    updateGoal,
    deleteGoal,
    linkProject,
    unlinkProject,
    linkHabit,
    unlinkHabit,
    linkTask,
    unlinkTask,
    addKeyResult,
    updateKeyResult,
    stepKeyResult,
    deleteKeyResult,
    addCheckIn,
    deleteCheckIn,
    /** Synchronous derived progress for one goal */
    getProgress,
    /** Synchronous derived progress for all goals, keyed by id */
    progressById,
  };
}
